import { isAfter, isBefore, isEqualTime } from "@suse-o11y/date-time";
import { isArray } from "@suse-o11y/is-array";
import { min } from "@suse-o11y/min";
import {
	type QueryParamConfig,
	decodeDelimitedArray,
	decodeString,
	encodeDelimitedArray,
} from "serialize-query-params";
import { type DateSchema, date, mixed, object } from "yup";

import { type RefreshInterval, toRefreshInterval } from "~/components/refresh-interval-selector/utils";

import {
	type TimeRange,
	createSpecificRange,
	fromSerializingTimeRange,
	isStaticTimeRangeKey,
	toSerializingTimeRange,
} from "../../app/state/view/timeline/TimeRange";
import { decodeTimeNumber, encodeTimeNumber } from "../../generic/Util";

export const VALIDATION_ERROR_MORE_THAN_HALF_YEAR = "is-range-is-more-than-half-year";

const TimestampParam: QueryParamConfig<Date | undefined, Date | undefined> = {
	encode: encodeTimeNumber,
	decode: (value) => {
		if (!value || isArray(value)) {
			return undefined;
		}
		return decodeTimeNumber(value);
	},
	equals: (valueA, valueB) => {
		if (valueA == null || valueB == null) {
			return valueA === valueB;
		}
		return isEqualTime(valueA, valueB);
	},
};

export const TimeRangeParam: QueryParamConfig<TimeRange | undefined, TimeRange | undefined> = {
	encode: (value) => {
		if (value == null) {
			return undefined;
		}
		const serialized = toSerializingTimeRange(value);
		if (typeof serialized === "string") {
			return serialized;
		}
		const encodedEnd = encodeTimeNumber(serialized.end);
		const fragments = [encodeTimeNumber(serialized.start) ?? null];
		if (encodedEnd != null) {
			fragments.push(encodedEnd);
		}
		return encodeDelimitedArray(fragments);
	},
	decode: (value) => {
		const fragments = decodeDelimitedArray(value);

		if (fragments != null) {
			const start = decodeTimeNumber(fragments[0] ?? undefined);
			const end = decodeTimeNumber(fragments[1] ?? undefined);

			if (start != null) {
				return fromSerializingTimeRange({ start, end });
			}
		}

		const stringResult = decodeString(value);
		if (stringResult != null) {
			const start = decodeTimeNumber(stringResult);
			if (start != null) {
				return fromSerializingTimeRange({ start });
			}
		}

		return stringResult != null && isStaticTimeRangeKey(stringResult)
			? fromSerializingTimeRange(stringResult)
			: undefined;
	},
	equals: (valueA, valueB) => {
		if (valueA == null || valueB == null) {
			return valueA === valueB;
		}
		return valueA === valueB;
	},
};

const RefreshIntervalParam: QueryParamConfig<RefreshInterval | undefined, RefreshInterval | undefined> = {
	encode: (value) => (value === "off" ? undefined : value),
	decode: toRefreshInterval,
};

export const timelineSchema = object({
	timestamp: date()
		.nullable()
		.test("is-before-now", "timestamp is not in past", (value) => {
			if (value == null) {
				return true;
			}
			const now = new Date();
			return isBefore(value, now) || isEqualTime(value, now);
		})
		.when("timeRange", (timeRange: TimeRange | undefined, schema: DateSchema) =>
			schema.test(
				"is-timestamp-with-end-range",
				"timeRange with end date should have timestamp present",
				(timestamp: Date | undefined) => {
					if (timestamp == null || timeRange == null) {
						return true;
					}
					if (!timeRange.isUntilNow() && timestamp == null) {
						return false;
					}
					return true;
				},
			),
		),
	timeRange: mixed<TimeRange>()
		.test("is-start-before-end", "timeRange start is not before end", (timeRange?: TimeRange) => {
			if (!timeRange?.isCustom) {
				return true;
			}
			return isBefore(timeRange.start(), timeRange.endDate());
		})
		.test("is-range-in-past", "timeRange is not in past", (timeRange?: TimeRange) => {
			if (!timeRange?.isCustom) {
				return true;
			}
			const dateNow = new Date();
			return isBefore(timeRange.start(), dateNow) && !isAfter(timeRange.endDate(), dateNow);
		})
		.test(VALIDATION_ERROR_MORE_THAN_HALF_YEAR, "timeRange is more than half year", (timeRange?: TimeRange) => {
			if (timeRange == null) {
				return true;
			}
			return timeRange.isWithinLimit();
		}),
});

export const timelineParamsMap = {
	timestamp: TimestampParam,
	timeRange: TimeRangeParam,
	refreshInterval: RefreshIntervalParam,
};

export interface TimelineQuery {
	timestamp?: Date;
	timeRange: TimeRange;
}

export function adjustTimeRangeAndTimestamp({ timestamp, timeRange }: TimelineQuery): {
	timeRange: TimeRange;
	timestamp?: Date;
} {
	const now = Date.now();
	const timestampMissingOrInvalid = timestamp == null || isAfter(timestamp, now); // timestamp is in the future
	const adjustTimeRangeNeeded = !timestampMissingOrInvalid;
	const adjustedTimeRange = createAdjustedTimeRange();

	function createAdjustedTimeRange() {
		if (timestamp == null || !adjustTimeRangeNeeded) {
			return timeRange; // no need to readjust timeRange for these cases
		}
		const startDate = timeRange.start();
		const endDate = timeRange.endDate();

		return createSpecificRange(startDate, min(now, endDate));
	}

	function createAdjustedTimestamp() {
		// put wrong timestamp to the end of specific time range
		if (!adjustedTimeRange.isUntilNow() && timestampMissingOrInvalid) {
			return adjustedTimeRange.endDate();
		}
		if (timestampMissingOrInvalid) {
			return undefined;
		}
		return timestamp;
	}

	return {
		timestamp: createAdjustedTimestamp(),
		timeRange: adjustedTimeRange,
	};
}
